import styled, { css } from 'styled-components';

interface ButtonProps {
  $variant?: 'primary' | 'secondary' | 'outline' | 'danger' | 'ghost';
  $size?: 'sm' | 'md' | 'lg';
  $fullWidth?: boolean;
}

export const Button = styled.button<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${({ theme }) => theme.shadows.sm};

  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `}

  ${({ $size = 'md', theme }) => {
    switch ($size) {
      case 'sm':
        return css`
          padding: 6px 12px;
          font-size: ${theme.fontSize.sm};
        `;
      case 'lg':
        return css`
          padding: 12px 28px;
          font-size: ${theme.fontSize.lg};
        `;
      default:
        return css`
          padding: 10px 20px;
          font-size: ${theme.fontSize.md};
        `;
    }
  }}

  ${({ $variant = 'primary', theme }) => {
    switch ($variant) {
      case 'secondary':
        return css`
          background-color: ${theme.colors.secondary};
          color: white;
          border-color: ${theme.colors.secondary};
        `;
      case 'outline':
        return css`
          background-color: ${theme.colors.surface};
          color: ${theme.colors.text};
          border-color: ${theme.colors.border};
        `;
      case 'danger':
        return css`
          background-color: ${theme.colors.error};
          color: white;
          border-color: ${theme.colors.error};
        `;
      case 'ghost':
        return css`
          background-color: transparent;
          color: ${theme.colors.text};
          border-color: transparent;
          box-shadow: none;
        `;
      default:
        return css`
          background-color: ${theme.colors.primary};
          color: white;
          border-color: ${theme.colors.primary};
        `;
    }
  }}

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.md};

    ${({ $variant = 'primary', theme }) => {
      switch ($variant) {
        case 'outline':
          return css`
            border-color: ${theme.colors.borderHover};
            background-color: ${theme.colors.surfaceHover};
          `;
        case 'ghost':
          return css`
            background-color: ${theme.colors.surfaceHover};
            box-shadow: none;
          `;
        case 'primary':
          return css`
            background-color: ${theme.colors.primaryHover};
            border-color: ${theme.colors.primaryHover};
          `;
        default:
          return '';
      }
    }}
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
